import React, { useMemo } from 'react';

import { StepperItem } from '@swyftx/aviary/atoms/StepperItem/StepperItem';

import { UserStore } from '@shared/store';

import { OnboardingStepId } from '@routes/Onboarding/types/Onboarding.types';

import { useOnboardingSelector } from '@Onboarding/Onboarding.context';
import { onboardingMachine } from '@Onboarding/Onboarding.machine';

import { AppFeature, useIsFeatureEnabled } from 'src/config';
import { StateNode } from 'xstate';

import { transformOnboardingStates } from './stepperUtils';

const OnboardingStepper: React.FC = () => {
  const { isFeatureEnabled } = useIsFeatureEnabled();
  const occupationIncomeEnabled = isFeatureEnabled(AppFeature.OnboardingWizardOccupationIncomeQuestions);

  const { userProfile } = UserStore.useUserStore;

  const currentState = useOnboardingSelector((state) => state.toStrings());
  const completedSteps = useOnboardingSelector((state) => state.context.completedSteps);

  const transformedSteps = useMemo(() => {
    const filterRules = {
      // Hides the deposit funds step if the user is applying for an entity account
      depositFunds: !userProfile?.userSettings?.applyForEntityAccount,
      'setupProfile.occupationIncome': occupationIncomeEnabled,
    } as Record<OnboardingStepId, boolean>;

    return transformOnboardingStates(onboardingMachine.states as Record<string, StateNode>)
      .filter((step) => {
        if (step.id in filterRules) {
          return filterRules[step.id];
        }
        return true;
      })
      .map((step) => {
        if (step.children) {
          const filteredChildren = step.children.filter((child) => {
            if (child.id in filterRules) {
              return filterRules[child.id];
            }
            return true;
          });
          return { ...step, children: filteredChildren };
        }

        return step;
      });
  }, [occupationIncomeEnabled, userProfile?.userSettings?.applyForEntityAccount]);

  const [activeRootStepId, activeStepId] = useOnboardingSelector((state) => state.toStrings());

  const activeStepOrder = useMemo<number>(() => {
    const activeRootStep = transformedSteps.find((s) => s.id === activeRootStepId);

    if (!activeRootStep) return 0;

    if (!activeStepId) return activeRootStep.order;

    const activeStep = activeRootStep.children.find((s) => s.id === activeStepId);

    if (!activeStep) return activeRootStep.order;

    return activeStep.order;
  }, [transformedSteps, activeRootStepId, activeStepId]);

  return (
    <aside className='grid gap-8'>
      {transformedSteps.map((step, index) => (
        <StepperItem
          key={step.id}
          step={step}
          activeOrder={activeStepOrder}
          translationKey='onboarding'
          stepNumber={index + 1}
          currentState={currentState}
          completedSteps={completedSteps}
        />
      ))}
    </aside>
  );
};

export { OnboardingStepper };
